<template>
    <div class="mx-auto container container--fluid">
        <v-snackbar v-model="snackbar" :top="true" :color="color" :timeout="5000">
            <span>{{ snackbar_msg }}</span>
            <v-btn dark text @click="snackbar = false">Close</v-btn>
        </v-snackbar>

        <v-card class="box">
            <v-toolbar dense class="toolbar" flat color="white">
                <v-toolbar-title>Room Allotment Report</v-toolbar-title>
                <v-divider class="mx-4" inset vertical></v-divider>
                <v-spacer></v-spacer>
            </v-toolbar>

            <div class="px-4 mt-4 mb-6">
                <table class="simple-exam-table">
                    <thead>
                        <tr>
                            <th>Exam Plan</th>
                            <th>Academic Year</th>
                            <th>Semester</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Exam Conduct Type</th>
                            <th>Stream</th>
                            <th>Standard</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{{ scheduleHeader.exam_name || '-' }}</td>
                            <td>{{ scheduleHeader.academic_year || '-' }}</td>
                            <td>{{ scheduleHeader.semester || '-' }}</td>
                            <td>{{ formatDate(scheduleHeader.start_date) }}</td>
                            <td>{{ formatDate(scheduleHeader.end_date) }}</td>
                            <td>{{ formatList(scheduleHeader.exam_conduct_types) }}</td>
                            <td>{{ formatList(scheduleHeader.streams) }}</td>
                            <td>{{ formatStandards(scheduleHeader.standards) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <v-data-table
                :headers="headers"
                :items="roomReportList"
                :search="search"
                hide-default-footer
                class="elevation-1 mytable custom-row-height"
                :loading="overlay"
            >
                <template v-slot:top>
                    <v-row dense class="px-2 pb-2">
                        <v-col cols="4" sm="3" md="3" class="mr-5">
                            <v-btn
                                rounded
                                color="success"
                                outlined
                                :loading="loader"
                                small
                                class="mt-2 ml-2"
                                @click="exportToExcel"
                            >
                                <v-icon left color="success">mdi-export</v-icon>
                                Export
                            </v-btn>

                            <v-btn
                                color="error"
                                rounded
                                outlined
                                small
                                class="mt-2 ml-2"
                                @click="exportToPDF"
                                :disabled="!roomReportList.length"
                            >
                                <v-icon left color="error">mdi-file-pdf-box</v-icon>
                                Export PDF
                            </v-btn>
                        </v-col>

                        <v-col sm="6" md="6" cols="12">
                            <v-text-field
                                v-model="search"
                                append-icon="search"
                                label="Search"
                                single-line
                                hide-details
                            ></v-text-field>
                        </v-col>
                    </v-row>
                </template>

                <template v-slot:item.sno="{ item }">
                    {{ roomReportList.indexOf(item) + 1 }}
                </template>

                <template v-slot:body.append>
                    <tr class="totals-row font-weight-bold grey lighten-4" v-if="roomReportList.length">
                        <td colspan="2" class="text-right">Total:</td>
                        <td class="text-center">{{ totalBenches }}</td>
                        <td></td>
                        <td class="text-center">{{ totalSeats }}</td>
                        <td class="text-center">{{ totalAllottedSeats }}</td>
                        <td class="text-center">{{ totalAvailableSeats }}</td>
                        <td colspan="4"></td>
                    </tr>
                </template>
            </v-data-table>

            <v-overlay :value="overlay">
                <v-progress-circular indeterminate size="64"></v-progress-circular>
            </v-overlay>
        </v-card>
    </div>
</template>

<script>
import axios from "axios";
import * as XLSX from "xlsx";

export default {
    name: "RoomAllotmentReport",
    data: () => ({
        overlay: false,
        loader: false,
        snackbar: false,
        snackbar_msg: "",
        color: "",
        search: "",
        exam_plan_id: null,
        scheduleHeader: {},
        org_basic_data:[],
        roomReportList: [],
        headers: [
            { text: "S.No", align: "center", value: "sno", sortable: true },
            { text: "Room No", align: "center", value: "room_number", sortable: true },
            { text: "Benches #", align: "center", value: "number_of_benches", sortable: true },
            { text: "Capacity Per Bench #", align: "center", value: "capacity_per_bench", sortable: true },
            { text: "Total Seats", align: "center", value: "total_seats", sortable: true },
            { text: "Allotted Seats", align: "center", value: "allotted_seats", sortable: true },
            { text: "Available Seats", align: "center", value: "available_seats", sortable: true },
            { text: "Supervisor", align: "center", value: "supervisor_name", sortable: true },
            { text: "Exam Plan", align: "center", value: "exam_plan_name", sortable: true },
            { text: "Academic Year", align: "center", value: "academic_year", sortable: true },
            { text: "Semester", align: "center", value: "semester", sortable: true }
        ]
    }),

    computed: {
        totalBenches() {
            return this.roomReportList.reduce((sum, item) => sum + Number(item.number_of_benches || 0), 0);
        },
        totalSeats() {
            return this.roomReportList.reduce((sum, item) => sum + Number(item.total_seats || 0), 0);
        },
        totalAllottedSeats() {
            return this.roomReportList.reduce((sum, item) => sum + Number(item.allotted_seats || 0), 0);
        },
        totalAvailableSeats() {
            return this.roomReportList.reduce((sum, item) => sum + Number(item.available_seats || 0), 0);
        }
    },

    methods: {
        formatDate(dateStr) {
            if (!dateStr) return "-";
            const date = new Date(dateStr);
            return isNaN(date.getTime())
                ? dateStr
                : date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
        },

        formatList(arr) {
            if (!arr || !arr.length) return "-";
            return arr.map(item => (typeof item === 'object' ? item.name : item)).filter(Boolean).join(", ");
        },

        formatStandards(arr) {
            if (!arr || !arr.length) return "-";
            return arr.map(item => (typeof item === 'object' ? item.standard : item)).filter(Boolean).join(", ");
        },

        loadReportData() {
            if (!this.exam_plan_id) {
                this.showSnackbar("#b71c1c", "Exam Plan ID missing.");
                return;
            }

            this.overlay = true;
            axios.post("/SeatingArrangement/getRoomAllotmentReportData", {
                exam_plan_id: Number(this.exam_plan_id)
            })
            .then(res => {
                if (res.data.msg === "200") {
                    this.scheduleHeader = res.data.schedule_data || {};
                    this.roomReportList = res.data.room_allotment_data || [];
                    this.org_basic_data = res.data.org_basic_data || [];
                } else {
                    this.showSnackbar("#b71c1c", res.data.text || "Failed to load report data.");
                }
            })
            .catch(err => {
                console.error("Report Fetch Error:", err);
                this.showSnackbar("#b71c1c", "Error fetching room allotment report.");
            })
            .finally(() => {
                this.overlay = false;
            });
        },

        exportToExcel() {
            this.loader = true;
            try {
                const data = this.roomReportList.map((item, idx) => ({
                    "S.No": idx + 1,
                    "Room No": item.room_number,
                    "Benches #": item.number_of_benches,
                    "Capacity Per Bench #": item.capacity_per_bench,
                    "Total Seats": item.total_seats,
                    "Allotted Seats": item.allotted_seats,
                    "Available Seats": item.available_seats,
                    "Supervisor": item.supervisor_name,
                    "Exam Plan": item.exam_plan_name,
                    "Academic Year": item.academic_year,
                    "Semester": item.semester
                }));

                if (data.length > 0) {
                    data.push({
                        "S.No": "",
                        "Room No": "Total:",
                        "Benches #": this.totalBenches,
                        "Capacity Per Bench #": "",
                        "Total Seats": this.totalSeats,
                        "Allotted Seats": this.totalAllottedSeats,
                        "Available Seats": this.totalAvailableSeats,
                        "Supervisor": "",
                        "Exam Plan": "",
                        "Academic Year": "",
                        "Semester": ""
                    });
                }

                const ws = XLSX.utils.json_to_sheet(data);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Room Allotment Report");
                XLSX.writeFile(wb, "Room_Allotment_Report.xlsx");
            } catch (err) {
                console.error("Excel Export Error:", err);
                this.showSnackbar("#b71c1c", "Failed to export Excel file.");
            } finally {
                this.loader = false;
            }
        },

        exportToPDF() {
        if (!this.roomReportList.length) {
            this.showSnackbar("#b71c1c", "No data available to export");
            return;
        }

        const org = this.org_basic_data || [];

        console.log("Organization data for PDF:", org);

        const escapeHtml = (value) => {
            return String(value == null ? "" : value)
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#39;");
        };

            const schoolName = escapeHtml(org.school_name || "");
            const trustName = escapeHtml(org.trust_name || "");
            const schoolLogo = org.school_logo || "";
            const trustLogo = org.trust_logo || "";

            const printWindow = window.open("", "_blank");

            if (!printWindow) {
                this.showSnackbar(
                    "#b71c1c",
                    "Please allow pop-ups to export the report"
                );
                return;
            }

            let html = `
                <html>
                <head>
                    <title>Room Allotment Report</title>
                    <style>
                        @page {
                            size: A4 landscape;
                            margin: 10mm;
                        }

                        body {
                            font-family: Arial, sans-serif;
                            font-size: 10px;
                            margin: 0;
                        }

                        h2 {
                            text-align: center;
                            margin: 0 0 10px 0;
                            font-size: 16px;
                        }

                        .header-table,
                        .report-table {
                            width: 100%;
                            border-collapse: collapse;
                        }

                        .header-table {
                            margin-bottom: 12px;
                        }

                        .header-table th,
                        .header-table td,
                        .report-table th,
                        .report-table td {
                            border: 1px solid #000;
                            padding: 5px;
                            text-align: center;
                            font-size: 12px;
                        }

                        .header-table th,
                        .report-table th {
                            font-weight: bold;
                            background: #f2f2f2;
                        }

                        .report-table tr {
                            page-break-inside: avoid;
                        }

                        .total-row {
                            font-weight: bold;
                        }

                        .report-header {
                            display: table;
                            width: 100%;
                            table-layout: fixed;
                            margin: 10px 0 25px;
                            text-align: center;
                        }

                        .report-header .logo,
                        .report-header .school-details {
                            display: table-cell;
                            vertical-align: middle;
                        }

                        .report-header .logo {
                            width: 15%;
                            text-align: center;
                        }

                        .report-header .logo img {
                            max-width: 85px;
                            max-height: 75px;
                            width: auto;
                            height: auto;
                            object-fit: contain;
                        }

                        .report-header .school-details {
                            width: 70%;
                        }

                        .trust-name {
                            font-size: 15px;
                            font-weight: bold;
                            margin-bottom: 5px;
                        }

                        .school-name {
                            font-size: 20px;
                            font-weight: bold;
                            text-transform: uppercase;
                            margin: 5px 0;
                        }

                        .report-title {
                            font-size: 16px;
                            font-weight: bold;
                            margin-top: 5px;
                        }


                    </style>
                </head>
                <body>

                <div style="
                    display:flex;
                    align-items:center;
                    justify-content:space-between;
                    width:100%;
                    box-sizing:border-box;
                    margin-bottom:40px;
                    margin-top:10px;
                ">

                    <img
                        src="${this.org_basic_data.trust_logo || ''}"
                        style="width:100px; height:90px; object-fit:contain; flex-shrink:0;"
                    />

                    <div style="flex:1; text-align:center; min-width:0;">
                        <h2>${this.org_basic_data.trust_name || ''}</h2>
                        <h2>${this.org_basic_data.school_name || ''}</h2>
                        <h2>Room Allotment Report</h2>
                    </div>

                    <img
                        src="${this.org_basic_data.school_logo || ''}"
                        style="width:100px; height:90px; object-fit:contain; flex-shrink:0;"
                    />

                </div>


                <table class="header-table">
                    <thead>
                        <tr>
                            <th>Exam Plan</th>
                            <th>Academic Year</th>
                            <th>Semester</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Exam Conduct Type</th>
                            <th>Stream</th>
                            <th>Standard</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>${this.scheduleHeader.exam_name || "-"}</td>
                            <td>${this.scheduleHeader.academic_year || "-"}</td>
                            <td>${this.scheduleHeader.semester || "-"}</td>
                            <td>${this.formatDate(this.scheduleHeader.start_date)}</td>
                            <td>${this.formatDate(this.scheduleHeader.end_date)}</td>
                            <td>${this.formatList(this.scheduleHeader.exam_conduct_types)}</td>
                            <td>${this.formatList(this.scheduleHeader.streams)}</td>
                            <td>${this.formatStandards(this.scheduleHeader.standards)}</td>
                        </tr>
                    </tbody>
                </table>

                <table class="report-table">
                    <thead>
                        <tr>
                            <th>S.No</th>
                            <th>Room No</th>
                            <th>Benches</th>
                            <th>Capacity Per Bench</th>
                            <th>Total Seats</th>
                            <th>Allotted Seats</th>
                            <th>Available Seats</th>
                            <th>Supervisor</th>
                            <th>Exam Plan</th>
                            <th>Academic Year</th>
                            <th>Semester</th>
                        </tr>
                    </thead>
                    <tbody>
            `;

            this.roomReportList.forEach((item, index) => {
                html += `
                    <tr>
                        <td>${index + 1}</td>
                        <td>${item.room_number || ""}</td>
                        <td>${item.number_of_benches || 0}</td>
                        <td>${item.capacity_per_bench || 0}</td>
                        <td>${item.total_seats || 0}</td>
                        <td>${item.allotted_seats || 0}</td>
                        <td>${item.available_seats || 0}</td>
                        <td>${item.supervisor_name || ""}</td>
                        <td>${item.exam_plan_name || ""}</td>
                        <td>${item.academic_year || ""}</td>
                        <td>${item.semester || ""}</td>
                    </tr>
                `;
            });

            html += `
                    <tr class="total-row">
                        <td colspan="2">Total:</td>
                        <td>${this.totalBenches}</td>
                        <td></td>
                        <td>${this.totalSeats}</td>
                        <td>${this.totalAllottedSeats}</td>
                        <td>${this.totalAvailableSeats}</td>
                        <td colspan="4"></td>
                    </tr>
                    </tbody>
                </table>

                </body>
                </html>
            `;

            printWindow.document.open();
            printWindow.document.write(html);
            printWindow.document.close();

            printWindow.onload = function() {
                printWindow.focus();
                printWindow.print();
            };
        },

        showSnackbar(clr, msg) {
            this.snackbar = true;
            this.color = clr;
            this.snackbar_msg = msg;
        }
    },

    mounted() {
        this.exam_plan_id = localStorage.getItem("exam_plan_id");
        if (this.exam_plan_id) {
            this.loadReportData();
        }
    }
};
</script>

<style scoped>
.v-text-field {
    padding-top: 0px;
    margin-top: 0px;
}

.simple-exam-table {
    width: 100%;
    border-collapse: collapse;
    background-color: #ffffff;
    text-align: center;
    border: 1px solid #dcdcdc;
}

.simple-exam-table th {
    background-color: #f2f2f2;
    color: #333333;
    font-weight: 600;
    font-size: 13px;
    padding: 12px 8px;
    border: 1px solid #dcdcdc;
}

.simple-exam-table td {
    padding: 12px 8px;
    font-size: 13px;
    color: #444444;
    border: 1px solid #dcdcdc;
}

.custom-row-height ::v-deep td {
    height: 52px !important;
    padding-top: 8px !important;
    padding-bottom: 8px !important;
}

.custom-row-height ::v-deep th {
    height: 48px !important;
}

.totals-row td {
    font-size: 13px;
    border-top: 2px solid #dcdcdc;
}
</style>